import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Info, X } from "lucide-react";
import {
  WARM_UP_ACCEPTANCE_NOTE,
  describeWarmUpSendOutcome,
} from "@/constants/warmUpSequenceDefaults";
import { getClientAuthHeaders } from "@/lib/apiAuth";

const PAGE_LIMIT = 50;
const ERROR_STATUSES = new Set(["error", "blocked", "uncertain"]);
const RETRYABLE_STATUSES = new Set(["error", "blocked"]);
const RETRY_CLOSED = new Set(["already_sent", "uncertain", "not_retryable"]);
const SAFE_CODE = /^[a-z][a-z0-9_]{0,63}$/;

const REASONS = {
  already_sent: "This step was already accepted.",
  uncertain: "The provider result is unknown. This send is not repeated.",
  paused: "Warm Up sending is paused.",
  test_mode: "Test mode is pausing Warm Up sends.",
  integration_unavailable: "The PIT connection is not available.",
  enrollment_unavailable: "This sequence is not active.",
  not_retryable: "This step has no definite send failure.",
  missing_channel: "This chat has no sendable channel.",
  unsupported_channel: "This chat channel cannot be used for Warm Up.",
  unsupported_merge_tag: "A merge tag in this message is not supported.",
  empty_merge_tag: "A merge tag in this message has no value.",
  provider_rejected: "The provider rejected this send.",
  provider_retry: "The provider temporarily refused this send.",
  chat_unavailable: "This chat is not available.",
  duplicate_native_ghl_chat: "This contact has more than one native chat.",
};

function safeText(value, max) {
  if (typeof value !== "string") return null;
  const cleaned = value.trim();
  if (cleaned === "" || cleaned.length > max || /[\r\n\u0000@]/.test(cleaned)) return null;
  return cleaned;
}

function safeId(value) {
  if (typeof value !== "string" || value.length > 128 || /\s/.test(value) || value.includes("@")) {
    return null;
  }
  return value === "" ? null : value;
}

let retrySequence = 0;

function newRetryRequestId() {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  retrySequence += 1;
  const bytes = new Uint8Array(16);
  if (cryptoApi && typeof cryptoApi.getRandomValues === "function") {
    cryptoApi.getRandomValues(bytes);
  }
  const stamp = Date.now();
  bytes[0] = retrySequence & 0xff;
  bytes[1] = (retrySequence >> 8) & 0xff;
  bytes[2] = stamp & 0xff;
  bytes[3] = (stamp >> 8) & 0xff;
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function safeCount(value) {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) return null;
  return value;
}

export function warmUpRetryAllowed(item) {
  return Boolean(item && RETRYABLE_STATUSES.has(item.status) && item.job_id);
}

export function warmUpErrorReason(item) {
  if (!item || !ERROR_STATUSES.has(item.status)) return null;
  const code = item.safe_error_code;
  if (typeof code === "string" && SAFE_CODE.test(code)) {
    return REASONS[code] || code;
  }
  return "Warm Up send was not accepted.";
}

export function visibleWarmUpLogItems(items) {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item) => item && typeof item === "object" && safeId(item.id) && safeId(item.message_id))
    .map((item) => ({
      id: item.id,
      created_at: safeText(item.created_at, 40),
      message_id: item.message_id,
      message_label: safeText(item.message_label, 160),
      status: safeText(item.status, 32),
      safe_error_code: typeof item.safe_error_code === "string" && SAFE_CODE.test(item.safe_error_code)
        ? item.safe_error_code
        : null,
      provider_message_id: safeId(item.provider_message_id),
      job_id: safeId(item.job_id),
      attempt_count: safeCount(item.attempt_count),
      automatic_attempt_count: safeCount(item.automatic_attempt_count),
    }))
    .sort((left, right) => String(right.created_at || "").localeCompare(String(left.created_at || ""))
      || String(right.id).localeCompare(String(left.id)));
}

function pageItems(data) {
  return visibleWarmUpLogItems(data?.items);
}

export default function WarmUpChatLog({ chatId, backendUrl = "" }) {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [retryResults, setRetryResults] = useState({});

  const loadPage = useCallback(async (before) => {
    const params = new URLSearchParams({ limit: String(PAGE_LIMIT) });
    if (before) params.set("before", before);
    const response = await axios.get(
      `${backendUrl}/api/agent/v1/chats/${chatId}/warm-up-log?${params.toString()}`,
      { headers: getClientAuthHeaders() }
    );
    return pageItems(response?.data);
  }, [backendUrl, chatId]);

  useEffect(() => {
    let cancelled = false;
    setItems([]);
    setHasMore(false);
    setLoadError(false);
    setOpen(false);
    setPendingId(null);
    setRetryResults({});
    (async () => {
      try {
        const page = await loadPage();
        if (cancelled) return;
        setItems(page);
        setHasMore(page.length === PAGE_LIMIT);
      } catch {
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadPage]);

  const loadOlder = async () => {
    const before = items[items.length - 1]?.id;
    if (!before || loading) return;
    setLoading(true);
    setLoadError(false);
    try {
      const page = await loadPage(before);
      setItems((current) => visibleWarmUpLogItems([...current, ...page]));
      setHasMore(page.length === PAGE_LIMIT);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const retry = async (item) => {
    if (!warmUpRetryAllowed(item) || pendingId) return;
    const requestId = newRetryRequestId();
    setPendingId(item.id);
    try {
      const response = await axios.post(
        `${backendUrl}/api/agent/v1/chats/${chatId}/warm-up-jobs/${item.job_id}/retry`,
        { retry_request_id: requestId },
        { headers: getClientAuthHeaders() }
      );
      const data = response?.data && typeof response.data === "object" ? response.data : {};
      const attempts = safeCount(data.attempt_count);
      const automatic = safeCount(data.automatic_attempt_count);
      if (data.status !== "queued" || attempts === null) {
        setRetryResults((current) => ({
          ...current,
          [item.id]: { text: "Warm Up did not accept this retry.", hide: false },
        }));
        return;
      }
      setItems((current) => current.map((row) => (
        row.id === item.id
          ? {
            ...row,
            attempt_count: attempts,
            automatic_attempt_count: automatic === null ? row.automatic_attempt_count : automatic,
          }
          : row
      )));
      const automaticText = automatic === null ? item.automatic_attempt_count : automatic;
      setRetryResults((current) => ({
        ...current,
        [item.id]: {
          text: `Queued. Attempts ${attempts}. Automatic ${automaticText}.`,
          hide: false,
        },
      }));
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const code = typeof detail === "string" && SAFE_CODE.test(detail) ? detail : null;
      setRetryResults((current) => ({
        ...current,
        [item.id]: {
          text: code ? (REASONS[code] || code) : "Warm Up did not accept this retry.",
          hide: code !== null && RETRY_CLOSED.has(code),
        },
      }));
    } finally {
      setPendingId(null);
    }
  };

  const newest = items[0];
  const reason = warmUpErrorReason(newest);

  return (
    <div className="flex items-center gap-2">
      {reason && (
        <span
          data-testid="warm-up-error-badge"
          title={reason}
          aria-label={reason}
          className="inline-flex text-red-400"
        >
          <AlertTriangle className="w-4 h-4" />
        </span>
      )}
      <button
        type="button"
        data-testid="warm-up-log-open"
        onClick={() => setOpen(true)}
        className="text-[11px] text-[#9CA3AF] underline-offset-2 hover:underline"
      >
        Warm Up log
      </button>
      <span
        data-testid="warm-up-acceptance-info"
        title={WARM_UP_ACCEPTANCE_NOTE}
        aria-label={WARM_UP_ACCEPTANCE_NOTE}
        className="inline-flex text-[#9CA3AF] cursor-help"
      >
        <Info className="w-3.5 h-3.5" />
      </span>
      {open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100]">
          <div
            role="dialog"
            aria-label="Warm Up log"
            data-testid="warm-up-log-modal"
            className="bg-[#1a1d21] border border-white/10 rounded-2xl p-6 max-w-lg w-full mx-4 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="font-heading font-semibold text-lg text-[#F3F4F6]">Warm Up log</h3>
                <p className="text-sm text-[#9CA3AF]">Deliveries from the last 30 days</p>
                <p className="text-sm text-[#9CA3AF]" data-testid="warm-up-log-acceptance-note">
                  {WARM_UP_ACCEPTANCE_NOTE}
                </p>
              </div>
              <button
                type="button"
                aria-label="Close Warm Up log"
                onClick={() => setOpen(false)}
                className="p-2 rounded-lg hover:bg-white/5 text-[#9CA3AF]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {items.length === 0 && !loadError && (
              <p data-testid="warm-up-log-empty" className="text-sm text-[#9CA3AF]">
                No Warm Up deliveries in the last 30 days.
              </p>
            )}
            {loadError && (
              <p data-testid="warm-up-log-error" className="text-sm text-red-300">
                Could not load the Warm Up log.
              </p>
            )}
            <ul className="max-h-80 overflow-y-auto space-y-3">
              {items.map((item) => {
                const itemReason = warmUpErrorReason(item);
                return (
                  <li key={item.id} data-testid={`warm-up-log-row-${item.id}`} className="text-sm text-[#F3F4F6]">
                    <p>{item.message_label || item.message_id}</p>
                    <p className="text-[#9CA3AF]">
                      {describeWarmUpSendOutcome(item.status)}
                      {item.created_at ? ` · ${item.created_at}` : ""}
                    </p>
                    {itemReason && <p className="text-red-300">{itemReason}</p>}
                    {item.attempt_count !== null && (
                      <p className="text-[#9CA3AF]">
                        Attempts {item.attempt_count}
                        {item.automatic_attempt_count !== null
                          ? ` · Automatic ${item.automatic_attempt_count}`
                          : ""}
                      </p>
                    )}
                    {warmUpRetryAllowed(item) && !retryResults[item.id]?.hide && (
                      <button
                        type="button"
                        data-testid={`warm-up-retry-${item.id}`}
                        onClick={() => retry(item)}
                        disabled={pendingId === item.id}
                        className="mt-1 text-[#01B47A] disabled:opacity-50"
                      >
                        Retry
                      </button>
                    )}
                    {retryResults[item.id]?.text && (
                      <p data-testid={`warm-up-retry-result-${item.id}`} className="text-[#9CA3AF]">
                        {retryResults[item.id].text}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
            {hasMore && (
              <button
                type="button"
                data-testid="warm-up-log-older"
                onClick={loadOlder}
                disabled={loading}
                className="mt-4 text-sm text-[#01B47A] disabled:opacity-50"
              >
                Older deliveries
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
